import Image from "next/image";
import { useState } from "react";
import OneClickModal from "./OneClickModal";
import { track } from "@/lib/metrika";
import { useLocale } from "@/lib/locale-context";
import { carSpecLabel } from "@/lib/car-spec-i18n";
import { featureLabelEn } from "@/lib/car-features-i18n";
import { CarPhotoBadge } from "./SiteBadge";
import type { BadgeCfg } from "@/lib/badge-types";

export type Car = {
  id: string;
  title: string;
  altTitle?: string;
  city?: string;
  image: string;
  rating: { value: number; count: number };
  badges?: ("sale" | "popular" | "new")[];
  features: { transmission: string; drive: string; fuel: string; seats: string };
  price: { current: string; old?: string; total?: string };
  // Метаданные для блока «Популярные автомобили»/фильтра по классам (не отображаются).
  carClass?: string;       // класс из 1С — ключ фильтра и агрегатов
  priceKopecks?: number;   // базовая суточная цена, копейки — для «от N»
  costKopecks?: number;    // ИТОГО за период из 1С, копейки (учитывает дробные сутки); 0 если не из поиска
  seatsNum?: number;       // места (число) — карточка класса
  bagsNum?: number;        // багаж (число)
  driveUnit?: string;      // привод (Передний/Задний/Полный) — фильтр
  currencySym?: string;    // символ валюты (₽/฿) — формат «от N»
  carFeatures?: { name: string; name_en?: string; sort?: number }[]; // характеристики; на фото показываем те, у кого sort 0
  year?: number;           // год выпуска — для сетки ТТХ в блоке брони
  color?: string;          // цвет — для свотча в сетке ТТХ
  station?: string;        // станция города — для бейджа города в общем автопарке
  filial?: string;         // филиал (офис) — вторая строка бейджа «город · филиал»
  filialEn?: string;       // AI-перевод филиала (для /en)
  isHybrid?: boolean;      // battery_capacity > 0 из 1С → фиолетовый бейдж PHEV на фото
  badgeCar?: BadgeCfg;     // настраиваемый в админке бейдж на фото (per-city)
};

const BADGE_STYLES: Record<string, string> = {
  sale: "bg-[#d52b1e] text-white",
  popular: "bg-primary text-white",
  new: "bg-[#19a86a] text-white",
};
const BADGE_LABEL: Record<string, string> = {
  sale: "Скидки",
  popular: "Популярная модель",
  new: "Новинка",
};
const BADGE_LABEL_EN: Record<string, string> = {
  sale: "Discount",
  popular: "Popular model",
  new: "New",
};

export default function CarCard({ car, onOpen, selected }: { car: Car; onOpen?: () => void; selected?: boolean }) {
  const { locale, t } = useLocale();
  // Филиал: на /en — AI-перевод (fallback на RU).
  const filialLabel = locale === "en" ? (car.filialEn || car.filial) : car.filial;
  // Характеристики-«хедлайнеры» (sort 0) — бейджи справа на фото.
  const featureBadges = (car.carFeatures || []).filter((f) => (f.sort ?? 0) === 0).slice(0, 3);
  // Фолбэк на плейсхолдер, если фото машины битое/отсутствует (часть машин в
  // общем автопарке без фото — иначе пустая карточка).
  const [imgSrc, setImgSrc] = useState(car.image || "/images/common/car-placeholder.jpg");
  const [oneClick, setOneClick] = useState(false); // модалка «Аренда в 1 клик»
  const openCar = () => { track("car_card_open", { city: car.station }); onOpen?.(); };
  const bookClick = () => { track("car_book_click", { city: car.station }); onOpen?.(); };
  return (
    // Внешний контейнер БЕЗ overflow — чтобы стрелка-указатель (как у Солнышко)
    // выходила за нижнюю границу карточки; сама карточка остаётся overflow-hidden.
    <div className="relative h-full">
      <div className={`flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_4px_12.5px_rgba(0,0,0,0.05)] transition hover:shadow-[var(--shadow-card-hover)]${selected ? " ring-2 ring-primary" : ""}`}>
      {/* photo */}
      <div
        className={`relative h-[200px] w-full${onOpen ? " cursor-pointer" : ""}`}
        onClick={openCar}
      >
        <Image
          src={imgSrc}
          alt={car.title}
          fill
          sizes="384px"
          className="object-cover"
          onError={() => setImgSrc("/images/common/car-placeholder.jpg")}
        />

        {/* Рейтинг скрыт: пока нет реальных отзывов по машинам (было «5.0 (0)»). */}

        {/* Бейдж «город · филиал» слева вверху на фото (на всех карточках). */}
        {car.city ? (
          <span className="absolute left-3 top-3 inline-flex flex-col rounded-xl bg-primary/95 px-2.5 py-1.5 text-white shadow-[0_2px_8px_rgba(0,57,166,0.35)] backdrop-blur-sm">
            <span className="flex items-center gap-1 whitespace-nowrap text-[13px] font-bold leading-[1.2]">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
                <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0Z" /><circle cx="12" cy="10" r="3" />
              </svg>
              {car.city}
            </span>
            {car.filial ? (
              <span className="mt-0.5 whitespace-nowrap pl-[16px] text-[11px] font-medium leading-[1.25] opacity-90">{locale === "en" ? "Office" : "Офис"} - {filialLabel}</span>
            ) : null}
          </span>
        ) : null}

        {/* badges top-right (акция/популярное/новинка) */}
        {car.badges?.length ? (
          <div className="absolute right-4 top-4 flex flex-col items-end gap-1">
            {car.badges.map((b) => (
              <span
                key={b}
                className={`rounded-3xl px-3 py-1 text-[14px] font-semibold ${BADGE_STYLES[b]}`}
              >
                {(locale === "en" ? BADGE_LABEL_EN : BADGE_LABEL)[b]}
              </span>
            ))}
          </div>
        ) : null}

        {/* PHEV: фиолетовый бейдж гибрида снизу слева */}
        {car.isHybrid ? (
          <span className="absolute bottom-2.5 left-3 whitespace-nowrap rounded-lg bg-[#6D28D9] px-2 py-1 text-[11px] font-semibold text-white shadow-[0_2px_8px_rgba(109,40,217,0.35)]">
            {locale === "en" ? "⚡ PHEV | 🔋 Charging | ⛽ Gasoline" : "⚡ PHEV | 🔋 Зарядка | ⛽ Бензин"}
          </span>
        ) : null}
      </div>

      {/* content */}
      <div className="flex min-h-[203px] flex-1 flex-col justify-between p-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <div
              className={`flex items-start gap-1 text-[16px] font-semibold leading-[1.2]${onOpen ? " cursor-pointer" : ""}`}
              onClick={openCar}
            >
              <span className="text-black transition hover:text-primary">{car.title}</span>
              {car.altTitle ? (
                <span className="text-text-muted">{car.altTitle}</span>
              ) : null}
            </div>
            {/* Город показываем бейджем на фото (см. выше), не текстом под названием. */}
          </div>

          {/* характеристики (sort 0) — чипы под фото, как на Солнышко */}
          {featureBadges.length ? (
            <div className="flex flex-wrap gap-1.5">
              {featureBadges.map((f, i) => (
                <span
                  key={i}
                  className="rounded-full bg-surface-2 px-2.5 py-0.5 text-[11px] font-semibold leading-[1.5] text-primary"
                >
                  {featureLabelEn(f.name, locale, f.name_en)}
                </span>
              ))}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3 text-[14px] font-medium leading-[1.3] text-text-muted">
            <span className="flex items-center gap-1">
              <span className="relative size-3.5">
                <Image src="/icons/transmission.svg" alt="" fill sizes="14px" />
              </span>
              {carSpecLabel(car.features.transmission, locale)}
            </span>
            <span className="flex items-center gap-1">
              <span className="relative size-3.5">
                <Image src="/icons/drive.svg" alt="" fill sizes="14px" />
              </span>
              {carSpecLabel(car.features.drive, locale)}
            </span>
            <span className="flex items-center gap-1">
              <span className="relative size-3.5">
                <Image src="/icons/fuel.svg" alt="" fill sizes="14px" />
              </span>
              {carSpecLabel(car.features.fuel, locale)}
            </span>
            <span className="flex items-center gap-1">
              <span className="relative size-3.5">
                <Image src="/icons/seat.svg" alt="" fill sizes="14px" />
              </span>
              {carSpecLabel(car.features.seats, locale)}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-1">
            <div className="flex flex-col whitespace-nowrap font-semibold">
              {car.price.old ? (
                <span className="text-[14px] leading-[1.25] tracking-[-0.42px] text-text-muted line-through">
                  {car.price.old}
                </span>
              ) : null}
              <span className="text-[24px] leading-[32px] tracking-[-0.72px] text-[#d52b1e]">
                {car.price.current}
              </span>
            </div>
            {car.price.total ? (
              <span className="pb-1 text-[12px] text-text-muted">
                {carSpecLabel(car.price.total, locale)}
              </span>
            ) : null}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { track("oneclick_open", { city: car.station }); setOneClick(true); }}
              className="flex-1 cursor-pointer rounded-xl bg-bg px-4 py-3 text-[16px] font-medium text-accent transition hover:bg-surface-2"
            >
              {t("oneclick.title")}
            </button>
            <button
              type="button"
              onClick={bookClick}
              className="flex-1 cursor-pointer rounded-xl bg-[#d52b1e] px-4 py-3 text-[16px] font-medium leading-6 text-white transition hover:bg-[#bf241a]"
            >
              {t("btn.book")}
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Стрелка-указатель вниз, когда карточка выбрана (как у Солнышко) —
          абсолютный CSS-треугольник в синем токене за пределами карточки. */}
      {selected && (
        <span className="absolute -bottom-[10px] left-1/2 z-10 size-0 -translate-x-1/2 border-x-[9px] border-x-transparent border-t-[10px] border-t-primary" />
      )}

      {oneClick && (
        <OneClickModal car={car} onClose={() => setOneClick(false)} />
      )}
    </div>
  );
}
